import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog } from "@/components/ui/dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { useWebsocketEndpoints, useCreateWebsocketEndpoint, useUpdateWebsocketEndpoint, useDeleteWebsocketEndpoint } from "@/hooks/use-websocket-endpoints";
import type { WebsocketEndpoint } from "@/types";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const columns: Column<WebsocketEndpoint>[] = [
  { key: "name", header: "Name" },
  { key: "uri", header: "URI", render: (w) => <span className="font-mono text-xs truncate max-w-[200px] block">{w.uri}</span> },
  { key: "protocols", header: "Protocols" },
  { key: "connection_type", header: "Conn Type" },
];

export default function WebsocketEndpointsPage() {
  const { data, isLoading } = useWebsocketEndpoints();
  const createMut = useCreateWebsocketEndpoint();
  const updateMut = useUpdateWebsocketEndpoint();
  const deleteMut = useDeleteWebsocketEndpoint();

  const defaultWs = {
    name: "",
    uri: "",
    protocols: "ari",
    connection_type: "per_call_config",
    connection_timeout: 10,
    reconnect_interval: 5,
    reconnect_attempts: 5,
    tls_enabled: false,
    headers: null as Record<string, string> | null,
    description: null as string | null,
  };

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WebsocketEndpoint | null>(null);
  const [form, setForm] = useState(defaultWs);

  const openCreate = () => { setEditing(null); setForm(defaultWs); setOpen(true); };
  const openEdit = (w: WebsocketEndpoint) => {
    setEditing(w);
    setForm({
      name: w.name,
      uri: w.uri,
      protocols: w.protocols,
      connection_type: w.connection_type,
      connection_timeout: w.connection_timeout,
      reconnect_interval: w.reconnect_interval,
      reconnect_attempts: w.reconnect_attempts,
      tls_enabled: w.tls_enabled,
      headers: w.headers ?? null,
      description: w.description ?? null,
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, { onSuccess: () => { setOpen(false); toast.success("WebSocket endpoint updated"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    } else {
      createMut.mutate(form as typeof form, { onSuccess: () => { setOpen(false); toast.success("WebSocket endpoint created"); }, onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
    }
  };

  const handleDelete = (w: WebsocketEndpoint) => {
    deleteMut.mutate(w.id, { onSuccess: () => toast.success("WebSocket endpoint deleted"), onError: (e) => toast.error(`Failed: ${(e as Error).message}`) });
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">WebSocket Endpoints</h1>
          <p className="text-sm text-muted-foreground mt-1">chan_websocket client connection configurations</p>
        </div>
        <Button onClick={openCreate} size="sm"><Plus size={14} /> Create WS Endpoint</Button>
      </div>

      <DataTable columns={columns} data={data} isLoading={isLoading} getRowKey={(w) => w.id} onEdit={openEdit} onDelete={handleDelete} emptyMessage="No WebSocket endpoints configured." />

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Edit WebSocket Endpoint" : "Create WebSocket Endpoint"}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name * <HelpIcon text="WebSocket client connection name. Referenced by Dial (WebSocket) actions. Example: stt-service, ai-bot." /></Label>
              <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="uri">URI * <HelpIcon text="WebSocket server URL to connect to. Example: wss://my-stt-service.example.com/v1/stream. Use ws:// for unencrypted, wss:// for TLS." /></Label>
              <Input id="uri" value={form.uri} onChange={(e) => updateField("uri", e.target.value)} placeholder="wss://example.com/ari/events" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="protocols">Protocols <HelpIcon text="WebSocket sub-protocols to negotiate. ari for Asterisk ARI WebSocket. Custom services may use other values." /></Label>
              <Input id="protocols" value={form.protocols} onChange={(e) => updateField("protocols", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="connection_type">Connection Type <HelpIcon text="client - persistent connection, always connected. per_call_config - connection created per call, reused if available." /></Label>
              <Select id="connection_type" value={form.connection_type} onChange={(e) => updateField("connection_type", e.target.value)}>
                <option value="per_call_config">Per Call Config</option>
                <option value="client">Client</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="connection_timeout">Connection Timeout <HelpIcon text="Seconds to wait for WebSocket connection to establish. Default 10. Increase for slow networks." /></Label>
              <Input type="number" id="connection_timeout" value={form.connection_timeout} onChange={(e) => updateField("connection_timeout", Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reconnect_interval">Reconnect Interval <HelpIcon text="Seconds between reconnection attempts after disconnect. Default 5." /></Label>
              <Input type="number" id="reconnect_interval" value={form.reconnect_interval} onChange={(e) => updateField("reconnect_interval", Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reconnect_attempts">Reconnect Attempts <HelpIcon text="Maximum reconnection attempts before giving up. Default 5. 0 = unlimited retries." /></Label>
              <Input type="number" id="reconnect_attempts" value={form.reconnect_attempts} onChange={(e) => updateField("reconnect_attempts", Number(e.target.value))} />
            </div>
          </div>

          <Switch id="tls_enabled" checked={form.tls_enabled} onChange={(e) => updateField("tls_enabled", e.target.checked)} label={<span>TLS Enabled <HelpIcon text="Enable TLS for the WebSocket connection. Must match the URI scheme (wss:// needs TLS)." /></span>} />

          <div className="space-y-1.5">
            <Label htmlFor="description">Description <HelpIcon text="Human-readable note about this WebSocket connection purpose." /></Label>
            <Textarea id="description" value={form.description ?? ""} onChange={(e) => updateField("description", e.target.value || null)} rows={2} />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}